import { memo, useState } from "react";

import { Button } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { Body } from "app/pages/activation/components/Widget/Body/Body";
import { Header } from "app/pages/activation/components/Widget/Header/Header";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { TrafficShareMetricSchema } from "app/common/benchmark/types";
import { BENCHMARK_TRACKER_REPORT_ROUTE } from "app/pages/benchmark-tracker";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { generateAutomationName } from "../../helpers/generateAutomationName";

import { TrafficShareWrapper, StyledWrapper, TrafficShareFooter } from "./styles";
import { textKeys } from "./texts";

type WidgetSchema = {
    isLoading: boolean;
    formattedDuration: string;
    defaultDuration: string;
    benchmarkId: string;
    primaryDomain: string;
    trafficShareData: TrafficShareMetricSchema;
};

export const Widget = memo(
    ({ isLoading, formattedDuration, defaultDuration, benchmarkId, primaryDomain, trafficShareData }: WidgetSchema) => {
        const track = activationTracking.home;

        const { country, timeGranularity, webSource } = proNavigator.getParams();
        const [isWidgetHover, setIsWidgetHover] = useState(false);

        const goToBenchmark = () => {
            proNavigator.go(BENCHMARK_TRACKER_REPORT_ROUTE, {
                key: benchmarkId,
                country,
                granularity: timeGranularity,
                webSource,
                duration: defaultDuration,
            });
        };

        const onTitleClick = () => {
            track.trackUserClickTrafficShareTitle();
            goToBenchmark();
        };

        const onCTAClick = () => {
            track.trackUserClickTrafficShareCTA();
            goToBenchmark();
        };

        const onWidgetClick = () => {
            track.trackUserClickTrafficShareWidget();
            goToBenchmark();
        };

        const handleHover = (isHovered) => {
            setIsWidgetHover(isHovered);
        };

        if (!trafficShareData && !isLoading) {
            return null;
        }

        return (
            <StyledWrapper data-automation={generateAutomationName("home", ["widget", "container"])}>
                <Header
                    formattedDuration={formattedDuration}
                    isWidgetHover={isWidgetHover}
                    isTitleLink
                    onClick={onTitleClick}
                />

                <TrafficShareWrapper>
                    <Body
                        primaryDomain={primaryDomain}
                        trafficShare={trafficShareData}
                        onClick={onWidgetClick}
                        handleHover={handleHover}
                        isLoading={isLoading}
                    />

                    <TrafficShareFooter>
                        <Button
                            onClick={onCTAClick}
                            type="flat"
                        >
                            {translate(textKeys.headerButtonText)}
                        </Button>
                    </TrafficShareFooter>
                </TrafficShareWrapper>
            </StyledWrapper>
        );
    },
);

Widget.displayName = "Widget";
